import { DOCUMENT } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Partner } from 'src/app/config/partner/_default';
import { AuthService } from 'src/app/core/auth/auth.service';
import { PartnerConfig } from 'src/app/core/interfaces/partner-config';
import { domainCookieExpiry, hostOnlyCookieExpiry, parentDomain, versionCookie, versionCookieDomain } from './version-cookie';

/** Lets the knob finish its slide before the page unloads, so the control reads as a switch and not as a link. */
const SLIDE_MS = 150;

/** Token role that lets a user try the new frontends. */
const NEW_FRONTEND_ROLE = 'NewFrontend';

/**
 * The receiver's sign page (`/document-signer/documents/<guid>/sign?token=…`). A logged-in user can open a sign
 * link too, and that page is for the receiver, not a page to switch versions on.
 */
const SIGN_PAGE = /\/sign(?:[?#]|$)/;

/**
 * The navbar's "New version" switch on banking and document signer and invoice and archive pages. It is always off here; turning it on
 * writes `new` to the area's version cookie and reloads this page, and the host's redirect answers that load with the
 * same page in the area's new frontend, whose own switch comes back the same way. Shown only while the partner config
 * carries the area's cookie (`bankingVersionCookie`, `documentSignerVersionCookie`, `conservationVersionCookie`,  `postboxVersionCookie`) and the user's token holds the
 * NewFrontend role, and never on a sign page.
 */
@Component({
  standalone: true,
  selector: 'app-version-switch',
  imports: [TranslatePipe],
  // `nav-item` gives the label the navbar's white link ink; the white border keeps the checked (primary) track
  // visible on the primary navbar.
  host: {
    class: 'd-flex align-items-center',
    '[class.nav-item]': 'visible',
    '(window:pageshow)': 'turnOffAfterBackForwardRestore($event)'
  },
  template: `
    @if (visible) {
      <div class="form-check form-switch d-flex align-items-center gap-2 ps-0 mb-0">
        <input class="form-check-input border-white m-0" type="checkbox" role="switch" id="version-switch"
          [checked]="on" (change)="switchToNew()">
        <label class="form-check-label nav-link p-0 text-nowrap" for="version-switch" style="cursor: pointer;">
          {{ 'version-switch.label' | translate }}
        </label>
      </div>
    }
  `
})
export class VersionSwitchComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly document = inject(DOCUMENT);

  public on = false;
  public hasNewFrontend = false;

  constructor() {
    if (!Partner.bankingVersionCookie && !Partner.documentSignerVersionCookie  && !Partner.conservationVersionCookie && !Partner.postboxVersionCookie) {
      return;
    }
    this.authService.subscribeUser().pipe(takeUntilDestroyed())
      .subscribe(user => this.hasNewFrontend = !!user?.Roles.includes(NEW_FRONTEND_ROLE));
  }

  public get visible(): boolean {
    return !!this.cookie && this.hasNewFrontend;
  }

  /** The version cookie of the area the current page belongs to, if the partner has one and the page can switch. */
  private get cookie(): PartnerConfig['bankingVersionCookie'] {
    const url = this.router.url;
    if (url.startsWith('/banking')) {
      return Partner.bankingVersionCookie;
    }
    if (url.startsWith('/document-signer') && !SIGN_PAGE.test(url)) {
      return Partner.documentSignerVersionCookie;
    }
    if (url.startsWith('/archive')) {
      return Partner.conservationVersionCookie;
    }
    
    if (url.startsWith('/invoices')) {
      return Partner.postboxVersionCookie;
    }
    return undefined;
  }

  public switchToNew(): void {
    const cookie = this.cookie;
    if (this.on || !cookie) {
      return;
    }
    this.on = true;
    const { hostname } = this.document.location;
    const domain = versionCookieDomain(cookie.domain, hostname);
    const staleDomain = domain ? '' : parentDomain(hostname);
    // Written before the reload, so the redirect already sees `new` on that very request.
    if (domain) {
      this.document.cookie = hostOnlyCookieExpiry(cookie.name);
    } else if (staleDomain) {
      this.document.cookie = domainCookieExpiry(cookie.name, staleDomain);
    }
    this.document.cookie = versionCookie(cookie.name, domain, 'new');
    setTimeout(() => this.document.location.reload(), SLIDE_MS);
  }

  // The back-forward cache restores this page with the switch still on, which would swallow every activation.
  public turnOffAfterBackForwardRestore(event: PageTransitionEvent): void {
    if (event.persisted) {
      this.on = false;
    }
  }
}
